import { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Container, Title, Text, Stack, Grid, Paper, Box,
  Loader, Center, Alert, Group, Badge, Timeline, ThemeIcon,
} from '@mantine/core';
import {
  IconCrown, IconAlertCircle, IconAnalyze, IconSearch,
  IconBulb, IconChartBar, IconShieldCheck, IconTarget,
  IconTrendingDown, IconRobot,
} from '@tabler/icons-react';
import {
  fetchAgents,
  selectAllAgents,
  selectBuilderAgents,
  selectOrchestratorAgent,
  selectAgentsLoading,
  selectAgentsError,
} from '../../redux/slices/agentsSlice';
import PipelineNode from './PipelineNode';
import AgentDetailPanel from './AgentDetailPanel';
import AgentEditorModal from './AgentEditorModal';

const PIPELINE_ORDER = [
  'context_analyst',
  'risk_discovery',
  'scenario_builder',
  'risk_evaluator',
  'security_analyst',
  'mitigating_actions_planner',
  'residual_analyst',
];

const AGENT_ICONS = {
  orchestrator:               IconCrown,
  context_analyst:            IconAnalyze,
  risk_discovery:             IconSearch,
  scenario_builder:           IconBulb,
  risk_evaluator:             IconChartBar,
  security_analyst:           IconShieldCheck,
  mitigating_actions_planner: IconTarget,
  residual_analyst:           IconTrendingDown,
};

export default function AgentManagerPage() {
  const dispatch = useDispatch();
  const allAgents = useSelector(selectAllAgents);
  const builderAgents = useSelector(selectBuilderAgents);
  const orchestrator = useSelector(selectOrchestratorAgent);
  const loading = useSelector(selectAgentsLoading);
  const error = useSelector(selectAgentsError);

  const [selectedAgentKey, setSelectedAgentKey] = useState('orchestrator');
  const [editingAgent, setEditingAgent] = useState(null);

  useEffect(() => {
    dispatch(fetchAgents());
  }, [dispatch]);

  // Auto-select orchestrator on initial load
  useEffect(() => {
    if (orchestrator && !selectedAgentKey) {
      setSelectedAgentKey('orchestrator');
    }
  }, [orchestrator, selectedAgentKey]);

  // Sort builder agents by pipeline order
  const orderedBuilderAgents = useMemo(() => {
    return [...builderAgents].sort((a, b) => {
      const ai = PIPELINE_ORDER.indexOf(a.agent_key);
      const bi = PIPELINE_ORDER.indexOf(b.agent_key);
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
    });
  }, [builderAgents]);

  // Find the currently selected agent
  const selectedAgent = useMemo(() => {
    if (selectedAgentKey === 'orchestrator') return orchestrator;
    return allAgents.find(a => a.agent_key === selectedAgentKey) || null;
  }, [selectedAgentKey, allAgents, orchestrator]);

  if (loading && !allAgents.length) {
    return (
      <Center h={400}>
        <Loader size="lg" />
      </Center>
    );
  }

  return (
    <Container size="xl" py="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Group gap="sm">
            <IconRobot size={24} stroke={1.5} color="var(--mantine-color-dimmed)" />
            <Title order={2} fw={500}>AI Agents</Title>
          </Group>
          <Group gap="xs">
            <Badge size="lg" variant="light" color="blue">
              {allAgents.length} Agents
            </Badge>
            <Badge size="lg" variant="light" color="violet">
              {orchestrator ? '1 Orchestrator' : 'No Orchestrator'}
            </Badge>
          </Group>
        </Group>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
            {error}
          </Alert>
        )}

        <Grid gutter="md">
          {/* Left pane — Pipeline */}
          <Grid.Col span={{ base: 12, md: 5 }}>
            <Box style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto' }}>
              <Stack gap={0}>
                {/* Orchestrator node */}
                {orchestrator && (
                  <Paper
                    p="sm"
                    radius="md"
                    withBorder
                    onClick={() => setSelectedAgentKey('orchestrator')}
                    style={{
                      cursor: 'pointer',
                      borderColor: selectedAgentKey === 'orchestrator'
                        ? 'var(--mantine-color-violet-5)' : undefined,
                      backgroundColor: selectedAgentKey === 'orchestrator'
                        ? 'var(--mantine-color-violet-0)' : undefined,
                      transition: 'all 150ms ease',
                    }}
                  >
                    <Group gap="sm">
                      <ThemeIcon size={36} radius="md" variant="light" color="violet">
                        <IconCrown size={20} />
                      </ThemeIcon>
                      <Stack gap={2}>
                        <Text fw={600} size="sm">{orchestrator.agent_name}</Text>
                        <Text size="xs" c="dimmed">Orchestrates the pipeline</Text>
                      </Stack>
                    </Group>
                  </Paper>
                )}

                {/* Connector line */}
                <Box
                  mx="auto"
                  style={{
                    width: 2,
                    height: 24,
                    backgroundColor: 'var(--mantine-color-gray-4)',
                  }}
                />

                {/* Builder agents as Timeline */}
                <Timeline
                  active={orderedBuilderAgents.length}
                  bulletSize={36}
                  lineWidth={2}
                >
                  {orderedBuilderAgents.map((agent) => {
                    const Icon = AGENT_ICONS[agent.agent_key] || IconRobot;
                    const isSelected = selectedAgentKey === agent.agent_key;

                    return (
                      <Timeline.Item
                        key={agent.agent_key}
                        bullet={
                          <ThemeIcon
                            size={36}
                            radius="xl"
                            variant={isSelected ? 'filled' : 'light'}
                            color="blue"
                          >
                            <Icon size={18} />
                          </ThemeIcon>
                        }
                      >
                        <PipelineNode
                          agent={agent}
                          selected={isSelected}
                          onClick={() => setSelectedAgentKey(agent.agent_key)}
                        />
                      </Timeline.Item>
                    );
                  })}
                </Timeline>
              </Stack>
            </Box>
          </Grid.Col>

          {/* Right pane — Detail */}
          <Grid.Col span={{ base: 12, md: 7 }}>
            <AgentDetailPanel
              agent={selectedAgent}
              onEdit={setEditingAgent}
            />
          </Grid.Col>
        </Grid>
      </Stack>

      <AgentEditorModal
        agent={editingAgent}
        opened={!!editingAgent}
        onClose={() => setEditingAgent(null)}
      />
    </Container>
  );
}
